<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AgendamentoAPI from 'dashboard/api/agendamento';
import SchedulingAPI from 'dashboard/api/scheduling';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  contact: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const contactName = computed(() => props.contact?.name || '');
const contactPhone = computed(
  () => props.contact?.phone_number?.replace(/\D/g, '') || ''
);
const isContactBlocked = computed(() => !!props.contact?.blocked);

const activeView = ref('avaliadoras');
const hasConfig = ref(false);
const configLoaded = ref(false);
const configMode = ref('api');
const isLocalMode = computed(() => configMode.value === 'local');

// ── API Mode State ──
const avaliadoras = ref([]);
const isLoadingAvaliadoras = ref(false);
const selectedAvaliadora = ref(null);
const selectedDate = ref(new Date().toISOString().split('T')[0]);
const horarios = ref([]);
const isLoadingHorarios = ref(false);
const selectedHorario = ref(null);
const agendarForm = ref({ descricao: 'Primeira avaliacao', servico: 0 });
const isAgendando = ref(false);
const isCancelando = ref(false);

// ── Local Mode State ──
const professionals = ref([]);
const isLoadingProfessionals = ref(false);
const selectedProfessional = ref(null);
const localDate = ref(new Date().toISOString().split('T')[0]);
const localSlots = ref([]);
const isLoadingSlots = ref(false);
const selectedSlot = ref(null);
const localBookingForm = ref({ service_name: '', notes: '' });
const isLocalBooking = ref(false);
const contactBookings = ref([]);
const isLoadingBookings = ref(false);
const profServices = ref([]);
const isLoadingProfServices = ref(false);
const selectedServices = ref([]);
const discount = ref(0);

// ── Config ──
async function loadConfig() {
  try {
    const { data } = await AgendamentoAPI.getConfig();
    if (data.configured === false) {
      hasConfig.value = false;
    } else {
      hasConfig.value = true;
      configMode.value = data.mode || 'api';
    }
  } catch {
    hasConfig.value = false;
  } finally {
    configLoaded.value = true;
  }
}

// ── API Mode Functions ──
async function loadAvaliadoras() {
  isLoadingAvaliadoras.value = true;
  try {
    const { data } = await AgendamentoAPI.getAvaliadoras();
    avaliadoras.value = data.avaliadoras || (Array.isArray(data) ? data : []);
  } catch {
    avaliadoras.value = [];
  } finally {
    isLoadingAvaliadoras.value = false;
  }
}

async function selectAvaliadora(avaliadora) {
  selectedAvaliadora.value = avaliadora;
  selectedHorario.value = null;
  loadHorarios();
}

async function loadHorarios() {
  if (!selectedDate.value) return;
  isLoadingHorarios.value = true;
  selectedHorario.value = null;
  try {
    const { data } = await AgendamentoAPI.getHorarios(selectedDate.value);
    const allSlots =
      data.slots_disponiveis || (Array.isArray(data) ? data : []);
    if (selectedAvaliadora.value?.id) {
      horarios.value = allSlots.filter(
        s => s.avaliadora_id === selectedAvaliadora.value.id
      );
    } else {
      horarios.value = allSlots;
    }
  } catch {
    horarios.value = [];
  } finally {
    isLoadingHorarios.value = false;
  }
}

async function submitAgendar() {
  if (!selectedHorario.value) return;
  isAgendando.value = true;
  try {
    await AgendamentoAPI.agendar({
      idevent: selectedHorario.value.idevent,
      nome: contactName.value,
      telefone: contactPhone.value,
      descricao: agendarForm.value.descricao,
      servico: agendarForm.value.servico,
    });
    useAlert(t('AGENDAMENTO.AGENDAR.SUCCESS'));
    selectedHorario.value = null;
  } catch {
    useAlert(t('AGENDAMENTO.AGENDAR.ERROR'));
  } finally {
    isAgendando.value = false;
  }
}

async function submitCancelar() {
  if (!window.confirm(t('AGENDAMENTO.CANCELAR.CONFIRM'))) return;
  isCancelando.value = true;
  try {
    await AgendamentoAPI.cancelar({ telefone: contactPhone.value });
    useAlert(t('AGENDAMENTO.CANCELAR.SUCCESS'));
  } catch {
    useAlert(t('AGENDAMENTO.CANCELAR.ERROR'));
  } finally {
    isCancelando.value = false;
  }
}

// ── Local Mode Functions ──
async function loadProfessionals() {
  isLoadingProfessionals.value = true;
  try {
    const { data } = await SchedulingAPI.getProfessionals(true);
    professionals.value = data;
  } catch {
    professionals.value = [];
  } finally {
    isLoadingProfessionals.value = false;
  }
}

async function selectProfessional(prof) {
  selectedProfessional.value = prof;
  selectedSlot.value = null;
  selectedServices.value = [];
  loadLocalSlots();
  loadProfServices(prof.id);
}

async function loadProfServices(profId) {
  if (!profId) { profServices.value = []; return; }
  isLoadingProfServices.value = true;
  try {
    const { data } = await SchedulingAPI.getProfessionalServices(profId);
    profServices.value = (data || []).filter(ps => ps.active);
  } catch {
    profServices.value = [];
  } finally {
    isLoadingProfServices.value = false;
  }
}

function addService(serviceId) {
  const ps = profServices.value.find(s => s.service_id === Number(serviceId));
  if (!ps || selectedServices.value.some(s => s.service_id === ps.service_id)) return;
  selectedServices.value.push({
    service_id: ps.service_id,
    service_name: ps.service_name,
    duration_minutes: ps.duration_minutes,
    price: ps.price,
  });
  localBookingForm.value.service_name = selectedServices.value.map(s => s.service_name).join(', ');
}

function removeService(index) {
  selectedServices.value.splice(index, 1);
  localBookingForm.value.service_name = selectedServices.value.map(s => s.service_name).join(', ');
}

const subtotalServicesPrice = computed(() =>
  selectedServices.value.reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0)
);

const totalServicesPrice = computed(() =>
  Math.max(0, subtotalServicesPrice.value - (parseFloat(discount.value) || 0))
);

const totalServicesDuration = computed(() =>
  selectedServices.value.reduce((sum, s) => sum + (parseInt(s.duration_minutes) || 0), 0)
);

async function loadLocalSlots() {
  if (!localDate.value || !selectedProfessional.value) return;
  isLoadingSlots.value = true;
  selectedSlot.value = null;
  try {
    const { data } = await SchedulingAPI.getAvailableSlots(
      localDate.value,
      selectedProfessional.value.id
    );
    localSlots.value = data.slots || [];
  } catch {
    localSlots.value = [];
  } finally {
    isLoadingSlots.value = false;
  }
}

async function submitLocalBooking() {
  if (!selectedSlot.value || !selectedProfessional.value) return;
  isLocalBooking.value = true;
  try {
    let endTime = selectedSlot.value.end_time;
    if (totalServicesDuration.value > 0) {
      const [h, m] = selectedSlot.value.start_time.split(':').map(Number);
      const totalMin = h * 60 + m + totalServicesDuration.value;
      endTime = `${String(Math.floor(totalMin / 60)).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}`;
    }

    const bookingData = {
      scheduling_professional_id: selectedProfessional.value.id,
      scheduled_date: localDate.value,
      start_time: selectedSlot.value.start_time,
      end_time: endTime,
      client_name: contactName.value,
      client_phone: contactPhone.value,
      service_name: localBookingForm.value.service_name,
      notes: localBookingForm.value.notes,
      contact_id: props.contact?.id,
      conversation_id: props.conversationId,
      total_amount: totalServicesPrice.value,
      discount_amount: parseFloat(discount.value) || 0,
    };

    if (selectedServices.value.length > 0) {
      let currentStart = selectedSlot.value.start_time;
      bookingData.items = selectedServices.value.map(svc => {
        const [sh, sm] = currentStart.split(':').map(Number);
        const eMin = sh * 60 + sm + parseInt(svc.duration_minutes);
        const svcEnd = `${String(Math.floor(eMin / 60)).padStart(2, '0')}:${String(eMin % 60).padStart(2, '0')}`;
        const item = {
          service_id: svc.service_id,
          service_name: svc.service_name,
          professional_id: selectedProfessional.value.id,
          duration_minutes: svc.duration_minutes,
          start_time: currentStart,
          end_time: svcEnd,
          price: svc.price,
        };
        currentStart = svcEnd;
        return item;
      });
    }

    await SchedulingAPI.createBooking(bookingData);
    useAlert(t('AGENDAMENTO.BOOKING.SUCCESS'));
    selectedSlot.value = null;
    selectedServices.value = [];
    discount.value = 0;
    localBookingForm.value = { service_name: '', notes: '' };
    loadLocalSlots();
    loadContactBookings();
  } catch {
    useAlert(t('AGENDAMENTO.BOOKING.ERROR'));
  } finally {
    isLocalBooking.value = false;
  }
}

async function loadContactBookings() {
  isLoadingBookings.value = true;
  try {
    const { data } = await SchedulingAPI.getBookings({ upcoming: true });
    const phone = contactPhone.value;
    const cId = props.contact?.id;
    contactBookings.value = data.filter(
      b =>
        b.contact_id === cId ||
        (phone && b.client_phone && b.client_phone.replace(/\D/g, '') === phone)
    );
  } catch {
    contactBookings.value = [];
  } finally {
    isLoadingBookings.value = false;
  }
}

async function cancelLocalBooking(booking) {
  if (!window.confirm(t('AGENDAMENTO.BOOKING.CANCEL_CONFIRM'))) return;
  try {
    await SchedulingAPI.cancelBooking(booking.id);
    useAlert(t('AGENDAMENTO.BOOKING.CANCEL_SUCCESS'));
    loadContactBookings();
  } catch {
    useAlert(t('AGENDAMENTO.CANCELAR.ERROR'));
  }
}

async function sendConfirmation(booking) {
  try {
    await SchedulingAPI.sendConfirmation(booking.id);
    useAlert(t('AGENDAMENTO.BOOKING.CONFIRMATION_SENT'));
    loadContactBookings();
  } catch (error) {
    if (error?.response?.status === 422) {
      useAlert(t('AGENDAMENTO.BOOKING.CONFIRMATION_NOT_CONFIGURED'));
    } else {
      useAlert(t('AGENDAMENTO.BOOKING.CONFIRMATION_ERROR'));
    }
  }
}

const statusColor = status =>
  ({
    scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    confirmed:
      'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    completed:
      'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    cancelled: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  })[status] || 'bg-slate-100 text-slate-600';

const viewTabs = computed(() =>
  isLocalMode.value
    ? ['professionals', 'schedule', 'bookings']
    : ['avaliadoras', 'agendar', 'cancelar']
);

watch(
  () => props.contact?.id,
  () => {
    selectedAvaliadora.value = null;
    selectedProfessional.value = null;
    selectedHorario.value = null;
    selectedSlot.value = null;
    horarios.value = [];
    localSlots.value = [];
    contactBookings.value = [];
    if (isLocalMode.value) loadContactBookings();
  }
);

watch(localDate, () => {
  if (selectedProfessional.value) loadLocalSlots();
});

onMounted(async () => {
  await loadConfig();
  if (hasConfig.value) {
    if (isLocalMode.value) {
      activeView.value = 'professionals';
      loadProfessionals();
      loadContactBookings();
    } else {
      activeView.value = 'avaliadoras';
      loadAvaliadoras();
    }
  }
});
</script>

<template>
  <div class="flex h-full flex-col overflow-auto p-4">
    <!-- Not configured -->
    <div
      v-if="configLoaded && !hasConfig"
      class="flex flex-1 flex-col items-center justify-center gap-3"
    >
      <span class="i-lucide-calendar-x2 size-12 text-slate-300" />
      <p class="text-sm text-slate-500">
        {{ t('AGENDAMENTO.NO_CONFIG') }}
      </p>
    </div>

    <div v-else-if="configLoaded && hasConfig" class="flex flex-col gap-4">
      <!-- Contact info -->
      <div
        class="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800"
      >
        <div>
          <p class="text-sm font-semibold text-slate-800 dark:text-white">
            {{ contactName }}
          </p>
          <p class="text-xs text-slate-500">
            {{ contactPhone || t('AGENDAMENTO.BOOKING.NO_PHONE') }}
          </p>
        </div>
        <span
          v-if="contactPhone"
          class="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900 dark:text-green-300"
        >
          {{ t('AGENDAMENTO.BOOKING.PHONE_OK') }}
        </span>
        <span
          v-else
          class="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900 dark:text-red-300"
        >
          {{ t('AGENDAMENTO.BOOKING.NO_PHONE') }}
        </span>
      </div>

      <!-- Blocked contact warning -->
      <div
        v-if="isContactBlocked"
        class="rounded-lg border border-red-300 bg-red-50 px-4 py-3 dark:border-red-800 dark:bg-red-900/30"
      >
        <p class="text-sm font-semibold text-red-600 dark:text-red-400">
          Cliente bloqueado para agendamento
        </p>
      </div>

      <!-- View toggle -->
      <div v-if="!isContactBlocked" class="flex gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
        <button
          v-for="view in viewTabs"
          :key="view"
          class="flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
          :class="
            activeView === view
              ? 'bg-white text-woot-600 shadow-sm dark:bg-slate-700 dark:text-woot-400'
              : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'
          "
          @click="activeView = view"
        >
          {{ t(`AGENDAMENTO.TABS.${view.toUpperCase()}`) }}
        </button>
      </div>

      <!-- ════════════════════════════════ -->
      <!-- API MODE -->
      <!-- ════════════════════════════════ -->

      <!-- API: Avaliadoras -->
      <div v-if="!isLocalMode && activeView === 'avaliadoras' && !isContactBlocked">
        <div class="mb-3 flex items-center justify-between">
          <h3 class="text-sm font-semibold text-slate-800 dark:text-white">
            {{ t('AGENDAMENTO.AVALIADORAS.TITLE') }}
          </h3>
          <button
            :disabled="isLoadingAvaliadoras"
            class="flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
            @click="loadAvaliadoras"
          >
            <span
              class="i-lucide-refresh-cw size-3"
              :class="{ 'animate-spin': isLoadingAvaliadoras }"
            />
            {{ t('AGENDAMENTO.AVALIADORAS.REFRESH') }}
          </button>
        </div>

        <div v-if="isLoadingAvaliadoras" class="flex justify-center py-6">
          <span class="i-lucide-loader-2 size-5 animate-spin text-woot-500" />
        </div>
        <div
          v-else-if="avaliadoras.length === 0"
          class="py-6 text-center text-xs text-slate-400"
        >
          {{ t('AGENDAMENTO.AVALIADORAS.EMPTY') }}
        </div>
        <div v-else class="space-y-2">
          <div
            v-for="avaliadora in avaliadoras"
            :key="avaliadora.id"
            class="cursor-pointer rounded-lg border p-3 transition-colors"
            :class="
              selectedAvaliadora?.id === avaliadora.id
                ? 'border-woot-400 bg-woot-50 dark:bg-woot-900/20 dark:border-woot-600'
                : 'border-slate-200 hover:border-woot-300 dark:border-slate-700 dark:hover:border-woot-700'
            "
            @click="selectAvaliadora(avaliadora)"
          >
            <div class="flex items-center gap-2">
              <span
                class="flex size-8 items-center justify-center rounded-full bg-woot-100 text-woot-600 dark:bg-woot-900 dark:text-woot-300"
              >
                <span class="i-lucide-user size-4" />
              </span>
              <div>
                <p class="text-sm font-medium text-slate-800 dark:text-white">
                  {{ avaliadora.nome }}
                </p>
                <p class="text-[10px] text-slate-400">
                  ID: {{ avaliadora.id }}
                </p>
              </div>
            </div>
          </div>

          <!-- Horarios da avaliadora -->
          <div
            v-if="selectedAvaliadora"
            class="mt-3 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800"
          >
            <div class="mb-2 flex items-center gap-2">
              <input
                v-model="selectedDate"
                type="date"
                class="flex-1 rounded border border-slate-200 bg-white px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                @change="loadHorarios"
              />
              <button
                :disabled="isLoadingHorarios"
                class="rounded border border-slate-200 px-2 py-1.5 text-xs text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
                @click="loadHorarios"
              >
                <span
                  class="i-lucide-refresh-cw size-3"
                  :class="{ 'animate-spin': isLoadingHorarios }"
                />
              </button>
            </div>

            <div v-if="isLoadingHorarios" class="flex justify-center py-4">
              <span
                class="i-lucide-loader-2 size-4 animate-spin text-woot-500"
              />
            </div>
            <div
              v-else-if="horarios.length === 0"
              class="py-3 text-center text-xs text-slate-400"
            >
              {{ t('AGENDAMENTO.AGENDAR.NO_HORARIOS') }}
            </div>
            <div v-else class="grid grid-cols-3 gap-1.5">
              <button
                v-for="slot in horarios"
                :key="slot.idevent"
                class="rounded border px-2 py-1.5 text-xs font-medium transition-colors"
                :class="
                  selectedHorario?.idevent === slot.idevent
                    ? 'border-woot-500 bg-woot-50 text-woot-700 dark:bg-woot-900 dark:text-woot-300'
                    : 'border-slate-200 text-slate-600 hover:border-woot-300 dark:border-slate-600 dark:text-slate-400'
                "
                @click="selectedHorario = slot"
              >
                {{ slot.start_time?.slice(0, 5) }}-{{
                  slot.end_time?.slice(0, 5)
                }}
              </button>
            </div>

            <!-- Quick schedule -->
            <div v-if="selectedHorario" class="mt-3 space-y-2">
              <div
                class="rounded bg-woot-50 px-3 py-2 text-xs text-woot-700 dark:bg-woot-900 dark:text-woot-300"
              >
                <strong
                  >{{ selectedHorario.start_time?.slice(0, 5) }}-{{
                    selectedHorario.end_time?.slice(0, 5)
                  }}</strong
                >
              </div>
              <input
                v-model="agendarForm.descricao"
                type="text"
                :placeholder="t('AGENDAMENTO.AGENDAR.DESCRICAO_PLACEHOLDER')"
                class="w-full rounded border border-slate-200 px-3 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
              />
              <button
                :disabled="isAgendando || !contactPhone"
                class="w-full rounded bg-woot-500 px-3 py-2 text-xs font-medium text-white hover:bg-woot-600 disabled:opacity-50"
                @click="submitAgendar"
              >
                {{
                  isAgendando
                    ? t('AGENDAMENTO.AGENDAR.SUBMITTING')
                    : t('AGENDAMENTO.AGENDAR.SUBMIT')
                }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- API: Agendar -->
      <div v-if="!isLocalMode && activeView === 'agendar' && !isContactBlocked">
        <div class="mb-3 flex items-center gap-2">
          <input
            v-model="selectedDate"
            type="date"
            class="flex-1 rounded border border-slate-200 bg-white px-3 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            @change="loadHorarios"
          />
          <button
            class="rounded bg-woot-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-woot-600"
            @click="
              selectedAvaliadora = null;
              loadHorarios();
            "
          >
            {{ t('AGENDAMENTO.AGENDAR.HORARIOS') }}
          </button>
        </div>
        <div v-if="isLoadingHorarios" class="flex justify-center py-6">
          <span class="i-lucide-loader-2 size-5 animate-spin text-woot-500" />
        </div>
        <div
          v-else-if="horarios.length === 0"
          class="py-6 text-center text-xs text-slate-400"
        >
          {{ t('AGENDAMENTO.AGENDAR.NO_HORARIOS') }}
        </div>
        <div v-else class="space-y-1.5">
          <button
            v-for="slot in horarios"
            :key="slot.idevent"
            class="flex w-full items-center justify-between rounded-lg border px-3 py-2 text-xs transition-colors"
            :class="
              selectedHorario?.idevent === slot.idevent
                ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
                : 'border-slate-200 hover:border-woot-300 dark:border-slate-600'
            "
            @click="selectedHorario = slot"
          >
            <span class="font-medium text-slate-800 dark:text-white">
              {{ slot.start_time?.slice(0, 5) }}-{{
                slot.end_time?.slice(0, 5)
              }}
            </span>
            <span class="text-slate-400">{{ slot.avaliadora }}</span>
          </button>
        </div>
        <div v-if="selectedHorario" class="mt-3 space-y-2">
          <input
            v-model="agendarForm.descricao"
            type="text"
            :placeholder="t('AGENDAMENTO.AGENDAR.DESCRICAO_PLACEHOLDER')"
            class="w-full rounded border border-slate-200 px-3 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
          />
          <button
            :disabled="isAgendando || !contactPhone"
            class="w-full rounded bg-woot-500 px-3 py-2 text-xs font-medium text-white hover:bg-woot-600 disabled:opacity-50"
            @click="submitAgendar"
          >
            {{
              isAgendando
                ? t('AGENDAMENTO.AGENDAR.SUBMITTING')
                : t('AGENDAMENTO.AGENDAR.SUBMIT')
            }}
          </button>
        </div>
      </div>

      <!-- API: Cancelar -->
      <div v-if="!isLocalMode && activeView === 'cancelar' && !isContactBlocked">
        <div
          class="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
        >
          <div class="mb-3 rounded-lg bg-slate-50 px-4 py-3 dark:bg-slate-800">
            <p class="text-sm text-slate-600 dark:text-slate-400">
              {{ t('AGENDAMENTO.CANCELAR.TITLE') }}
              <strong class="text-slate-800 dark:text-white">{{
                contactName
              }}</strong>
            </p>
            <p class="mt-1 text-xs text-slate-500">
              {{ t('AGENDAMENTO.CANCELAR.TELEFONE') }}:
              {{ contactPhone || 'N/A' }}
            </p>
          </div>
          <button
            :disabled="isCancelando || !contactPhone"
            class="w-full rounded bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
            @click="submitCancelar"
          >
            {{
              isCancelando
                ? t('AGENDAMENTO.CANCELAR.SUBMITTING')
                : t('AGENDAMENTO.CANCELAR.SUBMIT')
            }}
          </button>
        </div>
      </div>

      <!-- ════════════════════════════════ -->
      <!-- LOCAL MODE -->
      <!-- ════════════════════════════════ -->

      <!-- Local: Professionals -->
      <div v-if="isLocalMode && activeView === 'professionals' && !isContactBlocked">
        <div class="mb-3 flex items-center justify-between">
          <h3 class="text-sm font-semibold text-slate-800 dark:text-white">
            {{ t('AGENDAMENTO.PROFESSIONALS.TITLE') }}
          </h3>
          <button
            :disabled="isLoadingProfessionals"
            class="flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
            @click="loadProfessionals"
          >
            <span
              class="i-lucide-refresh-cw size-3"
              :class="{ 'animate-spin': isLoadingProfessionals }"
            />
          </button>
        </div>

        <div v-if="isLoadingProfessionals" class="flex justify-center py-6">
          <span class="i-lucide-loader-2 size-5 animate-spin text-woot-500" />
        </div>
        <div
          v-else-if="professionals.length === 0"
          class="py-6 text-center text-xs text-slate-400"
        >
          {{ t('AGENDAMENTO.PROFESSIONALS.EMPTY') }}
        </div>
        <div v-else class="space-y-2">
          <div
            v-for="prof in professionals"
            :key="prof.id"
            class="cursor-pointer rounded-lg border p-3 transition-colors"
            :class="
              selectedProfessional?.id === prof.id
                ? 'border-woot-400 bg-woot-50 dark:bg-woot-900/20 dark:border-woot-600'
                : 'border-slate-200 hover:border-woot-300 dark:border-slate-700'
            "
            @click="selectProfessional(prof)"
          >
            <div class="flex items-center gap-2">
              <span
                class="flex size-8 items-center justify-center rounded-full bg-woot-100 text-woot-600 dark:bg-woot-900 dark:text-woot-300"
              >
                <span class="i-lucide-user size-4" />
              </span>
              <div>
                <p class="text-sm font-medium text-slate-800 dark:text-white">
                  {{ prof.name }}
                </p>
                <p v-if="prof.specialty" class="text-[10px] text-slate-400">
                  {{ prof.specialty }}
                </p>
              </div>
            </div>
            <!-- Weekly hours summary -->
            <div
              v-if="prof.hours && prof.hours.length > 0"
              class="mt-2 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-slate-400"
            >
              <span
                v-for="hour in prof.hours.filter(h => h.active)"
                :key="hour.day_of_week"
              >
                {{ hour.day_name?.slice(0, 3) }}: {{ hour.start_time }}-{{
                  hour.end_time
                }}
              </span>
            </div>
          </div>

          <!-- Horarios do profissional selecionado -->
          <div
            v-if="selectedProfessional"
            class="mt-3 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800"
          >
            <div class="mb-2 flex items-center gap-2">
              <input
                v-model="localDate"
                type="date"
                class="flex-1 rounded border border-slate-200 bg-white px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
              />
              <button
                :disabled="isLoadingSlots"
                class="rounded border border-slate-200 px-2 py-1.5 text-xs text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
                @click="loadLocalSlots"
              >
                <span
                  class="i-lucide-refresh-cw size-3"
                  :class="{ 'animate-spin': isLoadingSlots }"
                />
              </button>
            </div>

            <div v-if="isLoadingSlots" class="flex justify-center py-4">
              <span
                class="i-lucide-loader-2 size-4 animate-spin text-woot-500"
              />
            </div>
            <div
              v-else-if="localSlots.length === 0"
              class="py-3 text-center text-xs text-slate-400"
            >
              {{ t('AGENDAMENTO.BOOKING.NO_SLOTS') }}
            </div>
            <div v-else class="grid grid-cols-3 gap-1.5">
              <button
                v-for="(slot, idx) in localSlots"
                :key="idx"
                class="rounded border px-2 py-1.5 text-xs font-medium transition-colors"
                :class="
                  selectedSlot === slot
                    ? 'border-woot-500 bg-woot-50 text-woot-700 dark:bg-woot-900 dark:text-woot-300'
                    : 'border-slate-200 text-slate-600 hover:border-woot-300 dark:border-slate-600 dark:text-slate-400'
                "
                @click="selectedSlot = slot"
              >
                {{ slot.start_time }}-{{ slot.end_time }}
              </button>
            </div>

            <!-- Quick local booking -->
            <div v-if="selectedSlot" class="mt-3 space-y-2">
              <div
                class="rounded bg-woot-50 px-3 py-2 text-xs text-woot-700 dark:bg-woot-900 dark:text-woot-300"
              >
                <strong>{{ selectedSlot.start_time }}-{{ selectedSlot.end_time }}</strong>
                | {{ selectedProfessional.name }}
              </div>
              <!-- Service selector -->
              <div v-if="isLoadingProfServices" class="text-xs text-slate-400">Carregando serviços...</div>
              <select
                v-else-if="profServices.length > 0"
                class="w-full rounded border border-slate-200 bg-white px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                @change="addService($event.target.value); $event.target.value = ''"
              >
                <option value="">+ Adicionar serviço...</option>
                <option
                  v-for="ps in profServices"
                  :key="ps.service_id"
                  :value="ps.service_id"
                  :disabled="selectedServices.some(s => s.service_id === ps.service_id)"
                >
                  {{ ps.service_name }} — {{ ps.duration_minutes }}min R${{ ps.price?.toFixed(2) }}
                </option>
              </select>
              <!-- Selected services (editable) -->
              <div v-if="selectedServices.length > 0" class="space-y-1">
                <div
                  v-for="(svc, idx) in selectedServices"
                  :key="svc.service_id"
                  class="rounded border border-green-200 bg-green-50 px-2 py-1.5 text-xs dark:border-green-800 dark:bg-green-900/20"
                >
                  <div class="flex items-center justify-between">
                    <span class="font-medium text-slate-700 dark:text-slate-200">{{ svc.service_name }}</span>
                    <button class="text-red-400 hover:text-red-600" @click="removeService(idx)">
                      <span class="i-lucide-x size-3" />
                    </button>
                  </div>
                  <div class="mt-1 flex items-center gap-2">
                    <div class="flex items-center gap-1">
                      <span class="text-slate-400">Tempo:</span>
                      <input
                        v-model.number="svc.duration_minutes"
                        type="number"
                        min="5"
                        step="5"
                        class="w-14 rounded border border-slate-200 bg-white px-1 py-0.5 text-center text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                      />
                      <span class="text-slate-400">min</span>
                    </div>
                    <div class="flex items-center gap-1">
                      <span class="text-slate-400">R$</span>
                      <input
                        v-model.number="svc.price"
                        type="number"
                        step="0.01"
                        min="0"
                        class="w-16 rounded border border-slate-200 bg-white px-1 py-0.5 text-right text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
                <!-- Discount -->
                <div class="flex items-center gap-2 text-xs">
                  <span class="text-slate-500">Desconto R$</span>
                  <input
                    v-model.number="discount"
                    type="number"
                    step="0.01"
                    min="0"
                    class="w-16 rounded border border-slate-200 bg-white px-1 py-0.5 text-right text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                  />
                </div>
                <!-- Totals -->
                <div class="flex items-center justify-between rounded bg-slate-50 px-2 py-1 text-xs dark:bg-slate-800">
                  <span class="text-slate-500">Total: {{ totalServicesDuration }}min</span>
                  <span class="font-bold text-slate-700 dark:text-slate-200">R$ {{ totalServicesPrice.toFixed(2) }}</span>
                </div>
              </div>
              <input
                v-model="localBookingForm.notes"
                type="text"
                placeholder="Observações..."
                class="w-full rounded border border-slate-200 px-3 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
              />
              <button
                :disabled="isLocalBooking || selectedServices.length === 0"
                class="w-full rounded bg-woot-500 px-3 py-2 text-xs font-medium text-white hover:bg-woot-600 disabled:opacity-50"
                @click="submitLocalBooking"
              >
                {{
                  isLocalBooking
                    ? t('AGENDAMENTO.BOOKING.SUBMITTING')
                    : t('AGENDAMENTO.BOOKING.SUBMIT')
                }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Local: Schedule (agendar) -->
      <div v-if="isLocalMode && activeView === 'schedule' && !isContactBlocked">
        <div class="mb-3 space-y-2">
          <select
            v-model="selectedProfessional"
            class="w-full rounded border border-slate-200 bg-white px-3 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            @change="selectedServices = []; selectedSlot = null; if (selectedProfessional) loadProfServices(selectedProfessional.id)"
          >
            <option :value="null">
              {{ t('AGENDAMENTO.BOOKING.SELECT_PROFESSIONAL') }}
            </option>
            <option v-for="prof in professionals" :key="prof.id" :value="prof">
              {{ prof.name }}
            </option>
          </select>
          <div class="flex items-center gap-2">
            <input
              v-model="localDate"
              type="date"
              class="flex-1 rounded border border-slate-200 bg-white px-3 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            />
            <button
              :disabled="isLoadingSlots || !selectedProfessional"
              class="rounded bg-woot-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-woot-600 disabled:opacity-50"
              @click="loadLocalSlots"
            >
              {{ t('AGENDAMENTO.AGENDAR.HORARIOS') }}
            </button>
          </div>
        </div>

        <div v-if="isLoadingSlots" class="flex justify-center py-6">
          <span class="i-lucide-loader-2 size-5 animate-spin text-woot-500" />
        </div>
        <div
          v-else-if="localSlots.length === 0 && selectedProfessional"
          class="py-6 text-center text-xs text-slate-400"
        >
          {{ t('AGENDAMENTO.BOOKING.NO_SLOTS') }}
        </div>
        <div v-else-if="localSlots.length > 0" class="space-y-1.5">
          <button
            v-for="(slot, idx) in localSlots"
            :key="idx"
            class="flex w-full items-center justify-between rounded-lg border px-3 py-2 text-xs transition-colors"
            :class="
              selectedSlot === slot
                ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
                : 'border-slate-200 hover:border-woot-300 dark:border-slate-600'
            "
            @click="selectedSlot = slot"
          >
            <span class="font-medium text-slate-800 dark:text-white">
              {{ slot.start_time }}-{{ slot.end_time }}
            </span>
            <span class="text-slate-400">{{
              slot.professional_name || selectedProfessional?.name
            }}</span>
          </button>
        </div>

        <div v-if="selectedSlot" class="mt-3 space-y-2">
          <!-- Service selector -->
          <div v-if="isLoadingProfServices" class="text-xs text-slate-400">Carregando serviços...</div>
          <select
            v-else-if="profServices.length > 0"
            class="w-full rounded border border-slate-200 bg-white px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            @change="addService($event.target.value); $event.target.value = ''"
          >
            <option value="">+ Adicionar serviço...</option>
            <option
              v-for="ps in profServices"
              :key="ps.service_id"
              :value="ps.service_id"
              :disabled="selectedServices.some(s => s.service_id === ps.service_id)"
            >
              {{ ps.service_name }} — {{ ps.duration_minutes }}min R${{ ps.price?.toFixed(2) }}
            </option>
          </select>
          <!-- Selected services -->
          <div v-if="selectedServices.length > 0" class="space-y-1">
            <div
              v-for="(svc, idx) in selectedServices"
              :key="svc.service_id"
              class="flex items-center justify-between rounded border border-green-200 bg-green-50 px-2 py-1 text-xs dark:border-green-800 dark:bg-green-900/20"
            >
              <span class="text-slate-700 dark:text-slate-200">
                {{ svc.service_name }}
                <span class="text-slate-400">{{ svc.duration_minutes }}min</span>
              </span>
              <div class="flex items-center gap-2">
                <span class="font-medium text-green-600">R$ {{ svc.price?.toFixed(2) }}</span>
                <button class="text-red-400 hover:text-red-600" @click="removeService(idx)">
                  <span class="i-lucide-x size-3" />
                </button>
              </div>
            </div>
            <div class="flex items-center justify-between rounded bg-slate-50 px-2 py-1 text-xs dark:bg-slate-800">
              <span class="text-slate-500">Total: {{ totalServicesDuration }}min</span>
              <span class="font-bold text-slate-700 dark:text-slate-200">R$ {{ totalServicesPrice.toFixed(2) }}</span>
            </div>
          </div>
          <input
            v-model="localBookingForm.notes"
            type="text"
            placeholder="Observações..."
            class="w-full rounded border border-slate-200 px-3 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-white"
          />
          <button
            :disabled="isLocalBooking || selectedServices.length === 0"
            class="w-full rounded bg-woot-500 px-3 py-2 text-xs font-medium text-white hover:bg-woot-600 disabled:opacity-50"
            @click="submitLocalBooking"
          >
            {{
              isLocalBooking
                ? t('AGENDAMENTO.BOOKING.SUBMITTING')
                : t('AGENDAMENTO.BOOKING.SUBMIT')
            }}
          </button>
        </div>
      </div>

      <!-- Local: Bookings (agendamentos do contato) -->
      <div v-if="isLocalMode && activeView === 'bookings' && !isContactBlocked">
        <div class="mb-3 flex items-center justify-between">
          <h3 class="text-sm font-semibold text-slate-800 dark:text-white">
            {{ t('AGENDAMENTO.TABS.BOOKINGS') }}
          </h3>
          <button
            :disabled="isLoadingBookings"
            class="flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
            @click="loadContactBookings"
          >
            <span
              class="i-lucide-refresh-cw size-3"
              :class="{ 'animate-spin': isLoadingBookings }"
            />
          </button>
        </div>

        <div v-if="isLoadingBookings" class="flex justify-center py-6">
          <span class="i-lucide-loader-2 size-5 animate-spin text-woot-500" />
        </div>
        <div
          v-else-if="contactBookings.length === 0"
          class="py-6 text-center text-xs text-slate-400"
        >
          {{ t('AGENDAMENTO.RECEPTION.NO_UPCOMING') }}
        </div>
        <div v-else class="space-y-2">
          <div
            v-for="booking in contactBookings"
            :key="booking.id"
            class="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="text-xs font-semibold text-slate-800 dark:text-white">
                  {{ booking.scheduled_date }} | {{ booking.start_time }}-{{
                    booking.end_time
                  }}
                </p>
                <p class="text-[10px] text-slate-500">
                  {{ booking.professional_name }}
                  <span v-if="booking.service_name">
                    | {{ booking.service_name }}</span
                  >
                </p>
              </div>
              <span
                :class="statusColor(booking.status)"
                class="rounded-full px-2 py-0.5 text-[10px] font-medium"
              >
                {{ t(`AGENDAMENTO.BOOKING.STATUS.${booking.status}`) }}
              </span>
            </div>
            <p v-if="booking.notes" class="mt-1 text-[10px] text-slate-400">
              {{ booking.notes }}
            </p>
            <div
              v-if="
                booking.status === 'scheduled' || booking.status === 'confirmed'
              "
              class="mt-2 flex gap-2"
            >
              <button
                class="flex-1 rounded border border-slate-200 px-2 py-1 text-[10px] text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-400"
                @click="sendConfirmation(booking)"
              >
                <span
                  class="i-lucide-send mr-1 inline-block size-3 align-middle"
                />
                {{ t('AGENDAMENTO.BOOKING.SEND_CONFIRMATION') }}
              </button>
              <button
                class="flex-1 rounded border border-red-200 px-2 py-1 text-[10px] text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400"
                @click="cancelLocalBooking(booking)"
              >
                <span
                  class="i-lucide-x mr-1 inline-block size-3 align-middle"
                />
                {{ t('AGENDAMENTO.BOOKING.CANCEL') }}
              </button>
            </div>
            <div
              v-if="booking.confirmation_sent"
              class="mt-1 flex items-center gap-1 text-[10px] text-green-600"
            >
              <span class="i-lucide-check-circle size-3" />
              {{ t('AGENDAMENTO.BOOKING.CONFIRMATION_SENT') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
